/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import cx from 'classnames';

import s from './styles.module.scss';

// Create icon lookup objects with just the icon names and their IDs
const iconNames = {
  accept: 'accept',
  apps: 'apps',
  appAdmin: 'app-admin',
  appMySoundtalks: 'app-my-soundtalks',
  approximatelyEqual: 'approximately-equal',
  arrowLeft: 'arrow-left',
  arrowLeftWhite: 'arrow-left-white',
  calendar: 'calendar',
  copy: 'copy',
  alertCircle: 'alert-circle',
  message: 'message',
  bell: 'bell',
  greenClose: 'green-close',
  home: 'home',
  search: 'search',
  searchLongHandle: 'search-long-handle',
  refresh: 'refresh',
  cough: 'cough',
  environmentalStatus: 'environmental-status',
  drag: 'drag',
  drop: 'drop',
  cloud: 'cloud',
  offlineNetwork: 'offline-network',
  alertStatus: 'alert-status',
  warningStatus: 'warning-status',
  warningCircle: 'warning-circle',
  warningTriangle: 'warning-triangle',
  successStatus: 'success-status',
  check: 'check',
  plusOutlined: 'plus-outlined',
  remove: 'remove',
  settingsOutlined: 'settings-outlined',
  settings: 'settings',
  weatherN: 'weather-n',
  weatherS: 'weather-s',
  weatherE: 'weather-e',
  weatherW: 'weather-w',
  weatherNE: 'weather-ne',
  weatherNW: 'weather-nw',
  weatherSE: 'weather-se',
  weatherSW: 'weather-sw',
  helpOutlined: 'help-o',
  helpOutlinedThick: 'help-outlined-thick',
  info: 'info',
  infoDark: 'info-dark',
  shield: 'shield',
  user: 'user',
  starOutlined: 'star-outlined',
  edit: 'edit',
  paperclip: 'paperclip',
  cloudDrizzle: 'cloud-drizzle',
  cloudLightning: 'cloud-lightning',
  cloudRain: 'cloud-rain',
  cloudSnow: 'cloud-snow',
  wind: 'wind',
  sun: 'sun',
  moon: 'moon',
  partlyCloudyDay: 'partly-cloudy-day',
  partlyCloudyNight: 'partly-cloudy-night',
  tornado: 'tornado',
  fog: 'fog',
  noteBlue: 'notes-blue',
  thermalShock: 'thermal-shock',
  heatStress: 'heat-stress',
  closeWhite: 'close-white',
  connectionLevel1: 'connection-level-1',
  connectionLevel2: 'connection-level-2',
  connectionLevel3: 'connection-level-3',
  connectionLevel4: 'connection-level-4',
  connectionUnknown: 'unknown-connection',
  activity: 'activity',
  audioVisualization: 'audio-visualization',
  clipboard: 'clipboard',
  grid: 'grid',
  list: 'list',
  locations: 'locations',
  play: 'play-circle',
  pause: 'pause-circle',
  download: 'download',
  close: 'x',
  animatedClock: 'clock-o',
  arrowUp: 'chevron-up',
  arrowDown: 'chevron-down',
  chevronLeft: 'chevron-left',
  chevronRight: 'chevron-right',
  downloadCloud: 'download-cloud',
  uploadCloud: 'upload-cloud',
  database: 'database',
  mapPin: 'map-pin',
  filter: 'filter',
  toolbox: 'toolbox',
  fattening: 'fattening',
  nursery: 'nursery',
  sow: 'sow',
  broiler: 'broiler',
  weanToFinish: 'wean-to-finish',
  boar: 'boar',
  gilt: 'gilt',
  recommendationDiagnostics: 'recommendation-diagnostics',
  recommendationTreatment: 'recommendation-treatment',
  recommendationOngoingTreatment: 'recommendation-ongoing-treatment',
  recommendationVeterinarian: 'recommendation-veterinarian',
  recommendationEnvironment: 'recommendation-environment',
  episodeDayRed: 'episode-day-red',
  episodeDayGreen: 'episode-day-green',
  episodeDayYellow: 'episode-day-yellow',
  episodeDayGrey: 'episode-day-grey',
  checkCircle: 'check-circle',
  minus: 'minus',
  plus: 'plus',
  monitor: 'monitor',
  monitorShining: 'monitor-shining',
  doa: 'doa',
  gateway: 'gateway',
  supplier: 'supplier',
  logo: 'logo',
  termometer: 'termometer',
  filterList: 'filter-list',
  cross: 'cross',
  pills: 'pills',
  pigHead: 'pig-head',
  pigMortality: 'pig-mortality',
  mail: 'mail',
  link: 'link',
  floorHeating: 'floor_heating',
  thermometerDrop: 'thermometer-drop',
  wechat: 'wechat',
} as const;

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  icon: string | { id: string } | null;
  className?: string;
  width?: string | number;
  height?: string | number;
}

const Icon = ({ icon, className, width = '24', height = '24', ...props }: IconProps) => {
  const iconName =
    (typeof icon === 'object' && icon !== null && 'id' in icon
      ? iconNames[icon.id as keyof typeof iconNames]
      : undefined) || icon;

  // When using SVG symbols with <use>, the viewBox comes from the symbol itself
  // We only need to set width/height on the wrapper SVG for sizing
  const svgProps = {
    ...props,
    // Default to 24px if no size specified (most icons are 24x24)
    width: width || '24',
    height: height || '24',
  };

  return (
    <svg {...svgProps} className={cx(s.icon, className)} data-testid={`icon-${iconName}`}>
      <use xlinkHref={`#icon-${iconName}`} />
    </svg>
  );
};

Icon.icons = iconNames;

export default Icon;
